import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { groupBy, sort } from 'remeda'
import { z } from 'zod'

import { LiquidityChartQueryResult, SavingsLiquidityData } from '@/config/chain/types'
import { sparkDataApiUrl } from '@/config/consts'
import { normalizedNumberSchema } from '@/domain/common/validation'

const tokenAddresses = {
  spusdc: '0x28B3a8fb53B741A8Fd78c0fb9A6B2393d896a43d',
  spusdt: '0xe2e7a17dFf93280dec073C995595155283e3C372',
} as const

export function sparkVaultV2LiquidityQueryOptionsFactory(type: 'spusdc' | 'spusdt') {
  const address = tokenAddresses[type]

  return function sparkVaultV2LiquidityChartQueryOptions(): LiquidityChartQueryResult {
    return {
      queryOptions: queryOptions({
        queryKey: sparkVaultV2LiquidityQueryKey(type),
        queryFn: () => sparkVaultV2LiquidityQueryFunction({ address }),
      }),
    }
  }
}

export function sparkVaultV2LiquidityQueryKey(type: string): QueryKey {
  return ['spark-vault-v2-liquidity', type]
}

export async function sparkVaultV2LiquidityQueryFunction({
  address,
}: {
  address: string
}): Promise<SavingsLiquidityData> {
  const res = await fetch(`${sparkDataApiUrl}/tokens/${address}/`)
  if (!res.ok) {
    throw new Error('Failed to fetch liquidity data')
  }
  return responseSchema.parse(await res.json()).data.liquidity
}

export const responseSchema = z.object({
  data: z.object({
    liquidity: z
      .array(
        z.object({
          liquidity: normalizedNumberSchema,
          address: z.string(),
          network: z.string(),
          protocol: z.string(),
        }),
      )
      .transform((data) => {
        const grouped = groupBy(data, (item) => item.protocol)
        const aggregated = Object.entries(grouped).map(([protocol, items]) => ({
          protocol,
          amount: items.reduce((sum, item) => sum.plus(item.liquidity), NormalizedNumber.ZERO),
        }))
        return sort(aggregated, (a, b) => b.amount.comparedTo(a.amount))
      }),
  }),
})
